/**
 * Articles de prose pour les rubriques Société / Économie / Diplomatie.
 *
 * Plutôt que des listes clé→valeur sèches, ces sections se lisent comme de
 * courts entrefilets de journal : les chiffres clés sont tissés dans des
 * phrases (en gras), assortis du verdict qualitatif. Toute l'information
 * chiffrée reste présente — elle est juste rendue lisible.
 */

import type { DerivedAggregates, WorldState } from "../engine/types";
import { HOME_COUNTRY } from "../engine/homeCountry";
import {
  indicatorText,
  indicatorValue,
  verdictAdjective,
} from "../engine/verbalize";
import { sparklineFor, trendFor } from "../engine/indicators";
import { useGame } from "../store/useGame";

interface Props {
  state: WorldState;
  aggregates: DerivedAggregates;
}

/** Étiquette qualitative d'une relation diplomatique (0..100). */
function relationLabel(score: number): string {
  if (score >= 70) return "allié";
  if (score >= 55) return "cordial";
  if (score >= 40) return "tendu";
  if (score >= 25) return "hostile";
  return "au bord de la rupture";
}

const CLIMATE_PHRASE: Record<string, string> = {
  calm: "au beau fixe",
  tense: "tendu",
  cold_war: "glacial",
  open_conflict: "ouvertement conflictuel",
};

export function SocieteArticle({ state, aggregates }: Props) {
  const { polity } = state;
  const pop = (aggregates.totalPopulation / 1e6).toFixed(2);
  const cadres = (aggregates.executivesCount / 1e6).toFixed(2);
  const ouvriers = (aggregates.workersCount / 1e6).toFixed(2);
  const retraites = (aggregates.retiredCount / 1e6).toFixed(2);
  const income = aggregates.weightedIncome.toFixed(0);
  const approval = polity.approval_rate;
  const real = aggregates.meanSatisfaction;
  const trust = aggregates.meanTrust;
  const gap = typeof approval === "number" ? approval - real : 0;
  const significant = Math.abs(gap) >= 8;
  const approvalVerdict = verdictAdjective("approval", state, aggregates);

  return (
    <div className="section-article">
      <p>
        {HOME_COUNTRY.labelCap} compte <strong>{pop} M</strong> d'habitants —{" "}
        <strong>{cadres} M</strong> de cadres, <strong>{ouvriers} M</strong>{" "}
        d'ouvriers et d'employés, <strong>{retraites} M</strong> de retraités —,
        pour un revenu moyen de <strong>{income} €</strong>.
      </p>
      <p>
        {typeof approval === "number" && (
          <>
            Dans les sondages, <strong>{approval.toFixed(0)} %</strong>{" "}
            approuvent l'action de l'exécutif
            {approvalVerdict && (
              <>
                {" "}
                — un niveau <em>{approvalVerdict}</em>
              </>
            )}
            .{" "}
          </>
        )}
        Mais la satisfaction réellement ressentie, à{" "}
        <strong>{real.toFixed(1)}</strong>,{" "}
        {significant ? (
          <>
            trahit un écart de{" "}
            <strong>{Math.abs(gap).toFixed(1)} points</strong> que{" "}
            {polity.regime === "dictatorship"
              ? "les chiffres officiels maquillent"
              : "l'image médiatique masque"}
            .{" "}
          </>
        ) : (
          <>colle d'assez près au sondage officiel. </>
        )}
        La confiance dans les institutions, elle, s'établit à{" "}
        <strong>{trust.toFixed(1)}</strong>.
      </p>
    </div>
  );
}

const SECTOR_LABEL: Record<string, string> = {
  agriculture: "Agriculture",
  industry: "Industrie",
  services: "Services",
  digital: "Numérique",
  energy: "Énergie",
  finance: "Finance",
};

/** Cotations affichées, dans l'ordre. `upIsGood` colore la flèche : une hausse
 *  du chômage est défavorable (rouge), une hausse du PIB favorable (bleu). */
const ECO_QUOTES: { id: string; label: string; upIsGood: boolean }[] = [
  { id: "gdp", label: "Produit intérieur", upIsGood: true },
  { id: "budget", label: "Solde budgétaire", upIsGood: true },
  { id: "debt_ratio", label: "Dette / PIB", upIsGood: false },
  { id: "inflation", label: "Inflation", upIsGood: false },
  { id: "unemployment", label: "Chômage", upIsGood: false },
  { id: "currency", label: "Monnaie", upIsGood: true },
];

const ECO_BAR_WIDTH = 12;

/**
 * Encart Économie rendu comme une page économique de journal : un chapô, une
 * table de cotations (sparkline en blocs + flèche de tendance colorée), et la
 * composition du PIB en barres typographiques. Les sparklines réutilisent
 * l'historique par tour via `sparklineFor`/`trendFor`.
 */
export function EconomieArticle({ state, aggregates }: Props) {
  const { economy } = state;
  const history = useGame((s) => s.indicatorHistory);
  const gdp = indicatorText("gdp", state, aggregates) ?? "—";

  const sectors = Object.entries(economy.gdp_by_sector)
    .map(([key, value]) => ({ key, value }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
  const sectorTotal = sectors.reduce((sum, s) => sum + s.value, 0) || 1;

  const trade = (economy.trade_balance / 1e9).toFixed(0);
  const keyRate = (economy.key_rate * 100).toFixed(2);

  // Énergie v1 — mix affiché en barres typographiques ; l'hydro/biomasse
  // est le solde. Absent des states antérieurs au 2026-07-02.
  const energy = economy.energy ?? null;
  const energyMix = energy
    ? [
        { label: "Fossile importé", share: energy.fossil_share },
        { label: "Nucléaire", share: energy.nuclear_share },
        { label: "Renouvelables", share: energy.renewable_share },
        {
          label: "Hydro & biomasse",
          share: Math.max(
            0,
            1 -
              energy.fossil_share -
              energy.nuclear_share -
              energy.renewable_share,
          ),
        },
      ]
    : [];

  return (
    <div className="section-article eco-article">
      <p className="eco-lede">
        Le pays produit <strong>{gdp}</strong> de richesse. Ci-dessous, ses
        grandes cotations et leur tendance sur les derniers mois.
      </p>

      <div className="eco-quotes">
        <div className="eco-head">Le pays en chiffres</div>
        {ECO_QUOTES.map((q) => {
          const value = indicatorText(q.id, state, aggregates) ?? "—";
          const h = history[q.id] ?? [];
          const { direction } = trendFor(h);
          const arrow =
            direction === "up" ? "▲" : direction === "down" ? "▼" : "—";
          const move =
            direction === "flat"
              ? "flat"
              : (direction === "up") === q.upIsGood
                ? "good"
                : "bad";
          return (
            <div className="eco-quote" key={q.id}>
              <span className="eco-quote-label">{q.label}</span>
              <span className="eco-quote-spark" aria-hidden="true">
                {sparklineFor(h, 8)}
              </span>
              <span className="eco-quote-value">{value}</span>
              <span
                className="eco-quote-arrow"
                data-move={move}
                aria-hidden="true"
              >
                {arrow}
              </span>
            </div>
          );
        })}
      </div>

      <div className="eco-sectors">
        <div className="eco-head">Composition de la richesse</div>
        {sectors.map((s) => {
          const pct = Math.round((s.value / sectorTotal) * 100);
          const filled = Math.max(
            0,
            Math.min(ECO_BAR_WIDTH, Math.round((pct / 100) * ECO_BAR_WIDTH)),
          );
          const bar = "█".repeat(filled) + "░".repeat(ECO_BAR_WIDTH - filled);
          return (
            <div className="eco-sector" key={s.key}>
              <span className="eco-sector-label">
                {SECTOR_LABEL[s.key] ?? s.key}
              </span>
              <span className="eco-sector-bar" aria-hidden="true">
                {bar}
              </span>
              <span className="eco-sector-pct">{pct}&nbsp;%</span>
            </div>
          );
        })}
      </div>

      {energy && (
        <div className="eco-sectors">
          <div className="eco-head">Le compte énergétique</div>
          {energyMix.map((m) => {
            const pct = Math.round(m.share * 100);
            const filled = Math.max(
              0,
              Math.min(ECO_BAR_WIDTH, Math.round(m.share * ECO_BAR_WIDTH)),
            );
            const bar = "█".repeat(filled) + "░".repeat(ECO_BAR_WIDTH - filled);
            return (
              <div className="eco-sector" key={m.label}>
                <span className="eco-sector-label">{m.label}</span>
                <span className="eco-sector-bar" aria-hidden="true">
                  {bar}
                </span>
                <span className="eco-sector-pct">{pct}&nbsp;%</span>
              </div>
            );
          })}
          <p className="eco-energy-note">
            Prix mondial de l'énergie&nbsp;:{" "}
            <strong>{Math.round(energy.world_price)}</strong>{" "}
            <em>
              (base 100
              {energy.world_price > 120
                ? " — flambée en cours, l'inflation s'en ressent"
                : ""}
              )
            </em>{" "}
            · indépendance{" "}
            <strong>{Math.round(energy.independence * 100)}/100</strong>
            {energy.shield > 0.05 && (
              <>
                {" "}
                · bouclier tarifaire <strong>actif</strong>{" "}
                <em>(la dette absorbe le surcoût)</em>
              </>
            )}
          </p>
        </div>
      )}

      <p className="eco-footer">
        <span>
          Balance commerciale <strong>{trade} Md€</strong>
        </span>
        <span>
          Taux directeur <strong>{keyRate} %</strong>
        </span>
      </p>
    </div>
  );
}

export function DiplomatieArticle({ state, aggregates }: Props) {
  const known = state.geopolitics.countries.filter((c) => c.known_to_player);
  const sorted = [...known].sort(
    (a, b) => b.political_relation - a.political_relation,
  );
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  const climate =
    CLIMATE_PHRASE[state.geopolitics.global_climate] ?? "incertain";
  const soft = indicatorText("soft_power_index", state, aggregates);
  const softVerdict = verdictAdjective("soft_power_index", state, aggregates);
  const depVerdict = verdictAdjective("foreign_dependence", state, aggregates);
  const depValue = indicatorValue("foreign_dependence", state, aggregates);

  return (
    <div className="section-article">
      <p>
        Le climat international est <em>{climate}</em>.
        {best && worst && best.label !== worst.label && (
          <>
            {" "}
            {HOME_COUNTRY.labelCap} peut s'appuyer sur{" "}
            <strong>{best.label}</strong> (
            {relationLabel(best.political_relation)}), mais ses relations avec{" "}
            <strong>{worst.label}</strong> sont{" "}
            {relationLabel(worst.political_relation)}.
          </>
        )}
      </p>
      <p>
        {soft && (
          <>
            Son rayonnement diplomatique atteint <strong>{soft}</strong>
            {softVerdict && (
              <>
                {" "}
                (<em>{softVerdict}</em>)
              </>
            )}
            .{" "}
          </>
        )}
        {depVerdict && depValue !== null && (
          <>
            Sa dépendance à l'étranger est jugée <em>{depVerdict}</em>.
          </>
        )}
      </p>
    </div>
  );
}
